/**
 * SeverityIcon — GTM-UX-14 (ClickUp 86carmhc6): shape-differentiated SVG
 * severity markers for the 5-level taxonomy (session-1 spec §1) plus the
 * two freshness-degraded states (ADR 0003 §2.2 Freshness), replacing the
 * emoji markers in frontend/src/utils/verdictCopy.ts's LEVEL_ICON/
 * NO_DATA_ICON/FRESHNESS_DISPLAY.
 *
 * Shape carries meaning independent of color (WCAG `color-not-only` —
 * session-1 spec §1 accessibility rule: "Action-needed vs Recommended must
 * differ by icon SHAPE... orange/yellow are indistinguishable for
 * colorblind users"). action_needed and recommended intentionally share
 * the SAME color token (amber — this codebase has only three semantic
 * status colors: success/danger/amber, see design.css) and are
 * distinguished ONLY by shape:
 *
 *   critical       — filled octagon        (--eg-danger)
 *   action_needed  — filled triangle        (--eg-amber)
 *   recommended    — outlined circle        (--eg-amber)
 *   ok             — check-shield           (--eg-success)
 *   no_data        — hollow dashed dot      (--eg-text-muted)
 *   stale          — outlined clock         (--eg-text-muted)
 *   processing     — outlined partial ring  (--eg-text-muted)
 *
 * One stroke style throughout (round caps/joins, 2px @ 24px viewbox) so the
 * set reads as one family. Renders `currentColor` — callers set color via
 * the `color` prop (CSS color/token value) rather than className overrides,
 * keeping color and shape decisions co-located at the call site.
 */

export type SeverityIconVariant =
  | 'critical'
  | 'action_needed'
  | 'recommended'
  | 'ok'
  | 'no_data'
  | 'stale'
  | 'processing'

export type SeverityIconSize = 'sm' | 'md' | 'lg'

/** Sizes as tokens — aligned to design-system/editorial-grid/tokens.ts's
 * spacing scale (spacing[4]=16, spacing[5]=20, spacing[6]=24) rather than
 * ad hoc pixel values. */
const SIZE_PX: Record<SeverityIconSize, number> = {
  sm: 16,
  md: 20,
  lg: 24,
}

/** Default color token per variant — callers may override via `color`. */
export const SEVERITY_ICON_COLOR: Record<SeverityIconVariant, string> = {
  critical: 'var(--eg-danger)',
  action_needed: 'var(--eg-amber)',
  recommended: 'var(--eg-amber)',
  ok: 'var(--eg-success)',
  no_data: 'var(--eg-text-muted)',
  stale: 'var(--eg-text-muted)',
  processing: 'var(--eg-text-muted)',
}

export interface SeverityIconProps {
  variant: SeverityIconVariant
  /** @default 'md' (20px) */
  size?: SeverityIconSize
  /** CSS color value. @default SEVERITY_ICON_COLOR[variant] */
  color?: string
  className?: string
}

const STROKE_COMMON = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

function Shape({ variant }: { variant: SeverityIconVariant }) {
  switch (variant) {
    case 'critical':
      // Filled octagon (stop-sign silhouette) — the only FILLED, angular
      // shape in the set, reserved for the single most urgent state.
      return (
        <path
          d="M8 2h8l6 6v8l-6 6H8l-6-6V8l6-6z"
          fill="currentColor"
          stroke="none"
        />
      )
    case 'action_needed':
      // Filled triangle — filled + angular, but 3-sided (not 8) so it
      // never reads as "critical" even in grayscale/small sizes.
      return (
        <path d="M12 3 L22 20 L2 20 Z" fill="currentColor" stroke="none" />
      )
    case 'recommended':
      // Outlined (hollow) circle — round, unfilled: the softest shape in
      // the set, matching its lowest-urgency rank.
      return <circle cx="12" cy="12" r="9" {...STROKE_COMMON} />
    case 'ok':
      // Check-shield — a protective shape, positive framing (session-1
      // spec §1: "Rejected spoofs are a POSITIVE, not an alarm").
      return (
        <>
          <path
            d="M12 2.5l7.5 3v6c0 5-3.2 8.6-7.5 10-4.3-1.4-7.5-5-7.5-10v-6l7.5-3z"
            {...STROKE_COMMON}
          />
          <path d="M8.5 12.2l2.4 2.4 4.6-5" {...STROKE_COMMON} />
        </>
      )
    case 'no_data':
      // Hollow DASHED dot — a smaller, dashed circle distinguishes "we
      // have nothing to show yet" from "recommended"'s solid outline
      // circle, even though both are round/hollow.
      return (
        <circle
          cx="12"
          cy="12"
          r="7"
          {...STROKE_COMMON}
          strokeDasharray="3 3.5"
        />
      )
    case 'stale':
      // Outlined clock — freshness-degraded, not a severity verdict.
      return (
        <>
          <circle cx="12" cy="12" r="9" {...STROKE_COMMON} />
          <path d="M12 7v5l3.5 2" {...STROKE_COMMON} />
        </>
      )
    case 'processing':
      // Outlined partial ring — an in-progress/indeterminate marker.
      return (
        <path
          d="M21 12a9 9 0 10-3.5 7.1"
          {...STROKE_COMMON}
        />
      )
  }
}

export function SeverityIcon({
  variant,
  size = 'md',
  color,
  className,
}: SeverityIconProps) {
  const px = SIZE_PX[size]
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      style={{ color: color ?? SEVERITY_ICON_COLOR[variant], flexShrink: 0 }}
    >
      <Shape variant={variant} />
    </svg>
  )
}
